import React from 'react';
import { Helmet } from 'react-helmet';
import { Box, Text, Button, Icon } from '@indeed/ifl-components';

interface ServerErrorPageProps {
    error?: {
        message: string;
        statusCode?: number;
        stack?: string;
        timestamp?: string;
        requestId?: string;
    };
    isDevelopment?: boolean;
    onRetry?: () => void; // Optional callback for client-side error boundary reset
}

const ServerErrorPage: React.FC<ServerErrorPageProps> = ({
    error,
    isDevelopment = false,
    onRetry,
}) => {
    const statusCode = error?.statusCode || 500;
    const title = statusCode === 404 ? 'Page Not Found' : 'Server Error';
    const defaultMessage =
        statusCode === 404
            ? 'The page you requested could not be found.'
            : 'We encountered an issue while processing your request.';

    const handleRefresh = () => {
        if (onRetry) {
            // If we have a retry callback (e.g., from error boundary), use it
            onRetry();
        } else if (typeof window !== 'undefined') {
            // Otherwise, fall back to page refresh (for server-side errors)
            window.location.reload();
        }
    };

    const handleGoHome = () => {
        if (typeof window !== 'undefined') {
            window.location.href = '/';
        }
    };

    return (
        <Box
            sx={{
                minBlockSize: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 4,
                backgroundColor: 'background.default',
            }}
        >
            <Helmet>
                <title>{title} - Indeed</title>
                <meta name="description" content={`Error ${statusCode}: ${title}`} />
            </Helmet>

            <Box
                sx={{
                    maxInlineSize: '600px',
                    inlineSize: '100%',
                    textAlign: 'center',
                }}
            >
                {/* Error Icon */}
                <Box sx={{ marginBlockEnd: 4 }}>
                    <Icon
                        name={statusCode === 404 ? 'search' : 'alert-circle'}
                        sx={{
                            color: 'status.error',
                            inlineSize: '64px',
                            blockSize: '64px',
                        }}
                    />
                </Box>

                {/* Error Title */}
                <Text
                    as="h1"
                    sx={{
                        fontSize: 6,
                        fontWeight: 'bold',
                        color: 'foreground.primary',
                        marginBlockEnd: 2,
                    }}
                >
                    {title}
                </Text>

                {/* Status Code */}
                <Text
                    sx={{
                        fontSize: 2,
                        color: 'status.error',
                        fontWeight: 'bold',
                        marginBlockEnd: 3,
                    }}
                >
                    Error {statusCode}
                </Text>

                {/* Error Message */}
                <Text
                    sx={{
                        fontSize: 3,
                        color: 'foreground.secondary',
                        marginBlockEnd: 4,
                        lineHeight: 1.6,
                    }}
                >
                    {error?.message || defaultMessage}
                </Text>

                {/* Development Error Details */}
                {isDevelopment && error && (
                    <Box
                        sx={{
                            backgroundColor: 'background.secondary',
                            padding: 4,
                            borderRadius: 2,
                            marginBlockEnd: 4,
                            textAlign: 'start',
                            border: '1px solid',
                            borderColor: 'border.default',
                        }}
                    >
                        <Text
                            sx={{
                                fontSize: 2,
                                fontWeight: 'bold',
                                color: 'foreground.primary',
                                marginBlockEnd: 2,
                            }}
                        >
                            🔍 Development Error Details
                        </Text>

                        {error.message && (
                            <Box sx={{ marginBlockEnd: 3 }}>
                                <Text
                                    sx={{
                                        fontSize: 1,
                                        fontWeight: 'bold',
                                        color: 'foreground.secondary',
                                        marginBlockEnd: 1,
                                    }}
                                >
                                    Error Message:
                                </Text>
                                <Text
                                    sx={{
                                        fontSize: 1,
                                        fontFamily: 'monospace',
                                        color: 'status.error',
                                        backgroundColor: 'background.tertiary',
                                        padding: 2,
                                        borderRadius: 1,
                                        overflowX: 'auto',
                                    }}
                                >
                                    {error.message}
                                </Text>
                            </Box>
                        )}

                        {error.timestamp && (
                            <Box sx={{ marginBlockEnd: 3 }}>
                                <Text
                                    sx={{
                                        fontSize: 1,
                                        fontWeight: 'bold',
                                        color: 'foreground.secondary',
                                        marginBlockEnd: 1,
                                    }}
                                >
                                    Timestamp:
                                </Text>
                                <Text
                                    sx={{
                                        fontSize: 1,
                                        fontFamily: 'monospace',
                                        color: 'foreground.primary',
                                    }}
                                >
                                    {error.timestamp}
                                </Text>
                            </Box>
                        )}

                        {error.requestId && (
                            <Box sx={{ marginBlockEnd: 3 }}>
                                <Text
                                    sx={{
                                        fontSize: 1,
                                        fontWeight: 'bold',
                                        color: 'foreground.secondary',
                                        marginBlockEnd: 1,
                                    }}
                                >
                                    Request ID:
                                </Text>
                                <Text
                                    sx={{
                                        fontSize: 1,
                                        fontFamily: 'monospace',
                                        color: 'foreground.primary',
                                    }}
                                >
                                    {error.requestId}
                                </Text>
                            </Box>
                        )}

                        {error.stack && (
                            <Box>
                                <Text
                                    sx={{
                                        fontSize: 1,
                                        fontWeight: 'bold',
                                        color: 'foreground.secondary',
                                        marginBlockEnd: 1,
                                    }}
                                >
                                    Stack Trace:
                                </Text>
                                <Text
                                    as="pre"
                                    sx={{
                                        fontSize: 0,
                                        fontFamily: 'monospace',
                                        color: 'foreground.secondary',
                                        backgroundColor: 'background.tertiary',
                                        padding: 2,
                                        borderRadius: 1,
                                        overflowX: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        maxBlockSize: '300px',
                                        overflowY: 'auto',
                                    }}
                                >
                                    {error.stack}
                                </Text>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Action Buttons */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 3,
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                    }}
                >
                    <Button
                        variant="primary"
                        onClick={handleRefresh}
                        sx={{ minInlineSize: '120px' }}
                    >
                        Try Again
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={handleGoHome}
                        sx={{ minInlineSize: '120px' }}
                    >
                        Go Home
                    </Button>
                </Box>

                {/* Production Help Text */}
                {!isDevelopment && (
                    <Box sx={{ marginBlockStart: 4 }}>
                        <Text
                            sx={{
                                fontSize: 1,
                                color: 'foreground.tertiary',
                                fontStyle: 'italic',
                            }}
                        >
                            If this issue persists, please contact support.
                            {error?.requestId && (
                                <>
                                    <br />
                                    Reference ID: {error.requestId}
                                </>
                            )}
                        </Text>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ServerErrorPage;
